import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Project } from '../../types';
import { updateProject } from '../../services/projectService';
import { searchGroups, getGroup, createGroupWithTeam, M365Group } from '../../services/groupService';
import BoardModal, { BMCancelButton, BMDangerButton, BM_LABEL, BM_INPUT } from '../BoardModal';

const ADMIN_URL = 'https://admin.teams.microsoft.com';

export default function M365GroupSection({ pca, project, onChanged }: {
  pca: IPublicClientApplication; project: Project; onChanged: () => void;
}) {
  const groupId = project.dagrun_pm_m365groupid || null;
  const [group, setGroup] = useState<M365Group | null>(null);
  const [loadingGroup, setLoadingGroup] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [showUnlink, setShowUnlink] = useState(false);
  const [creating, setCreating] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!groupId) { setGroup(null); return; }
    let cancelled = false;
    setLoadingGroup(true);
    getGroup(pca, groupId)
      .then(g => { if (!cancelled) setGroup(g); })
      .catch(() => { if (!cancelled) setGroup(null); })
      .finally(() => { if (!cancelled) setLoadingGroup(false); });
    return () => { cancelled = true; };
  }, [pca, groupId]);

  const linkGroup = async (id: string) => {
    await updateProject(pca, project.dagrun_pm_projectid, { dagrun_pm_m365groupid: id });
    setShowLink(false);
    onChanged();
  };

  const createGroup = async () => {
    setCreating(true); setError(null);
    try {
      const id = await createGroupWithTeam(pca, project.dagrun_pm_name);
      await updateProject(pca, project.dagrun_pm_projectid, { dagrun_pm_m365groupid: id });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the Microsoft 365 Group.');
    } finally {
      setCreating(false);
    }
  };

  const unlinkGroup = async () => {
    setUnlinking(true);
    try {
      await updateProject(pca, project.dagrun_pm_projectid, { dagrun_pm_m365groupid: null });
      setShowUnlink(false);
      onChanged();
    } finally {
      setUnlinking(false);
    }
  };

  return (
    <div className="card" style={{ padding: 20, marginTop: 24 }}>
      <div className="eyebrow" style={{ marginBottom: 12 }}>MICROSOFT 365 GROUP</div>

      {creating ? (
        <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>Creating Microsoft 365 Group…</div>
      ) : !groupId ? (
        // ── State A: no group linked ──
        <>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 14 }}>
            No Microsoft 365 Group linked to this project.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn-secondary" onClick={() => { setError(null); setShowLink(true); }}>Link Existing Group</button>
            <button type="button" className="btn-primary" onClick={createGroup}>Create New Group for This Project</button>
          </div>
          {error && <div style={{ marginTop: 12, color: 'var(--danger)', fontSize: 12 }}>{error}</div>}
        </>
      ) : (
        // ── State B: group linked ──
        <>
          {loadingGroup ? (
            <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>Loading group…</div>
          ) : (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>{group?.displayName ?? 'Linked group'}</div>
              <div style={{ fontSize: 12, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>{group?.mail ?? groupId}</div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <a href={ADMIN_URL} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 13, color: '#0E7490', fontWeight: 600 }}>View in Teams Admin Center ↗</a>
            <button type="button" className="btn-ghost" onClick={() => setShowUnlink(true)}
              style={{ color: 'var(--danger)' }}>Unlink Group</button>
          </div>
        </>
      )}

      {showLink && (
        <LinkExistingGroupModal pca={pca} onClose={() => setShowLink(false)} onPick={linkGroup} />
      )}

      {showUnlink && (
        <BoardModal open title="Unlink Microsoft 365 Group" onClose={() => !unlinking && setShowUnlink(false)}
          footer={<>
            <BMCancelButton onClick={() => setShowUnlink(false)} disabled={unlinking} />
            <BMDangerButton onClick={unlinkGroup} disabled={unlinking}>{unlinking ? 'Unlinking…' : 'Unlink'}</BMDangerButton>
          </>}>
          This will only remove the link in Dagrun PM. The Microsoft 365 Group itself will not be deleted.
        </BoardModal>
      )}
    </div>
  );
}

function LinkExistingGroupModal({ pca, onClose, onPick }: {
  pca: IPublicClientApplication; onClose: () => void; onPick: (groupId: string) => Promise<void>;
}) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<M365Group[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounce = useRef<number | null>(null);

  const run = useCallback((q: string) => {
    if (debounce.current) window.clearTimeout(debounce.current);
    if (q.trim().length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true); setError(null);
    debounce.current = window.setTimeout(async () => {
      try { setResults(await searchGroups(pca, q.trim())); }
      catch (e) { setError(e instanceof Error ? e.message : 'Search failed'); setResults([]); }
      finally { setSearching(false); }
    }, 300);
  }, [pca]);

  const pick = async (g: M365Group) => {
    setSaving(true); setError(null);
    try { await onPick(g.id); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not link the group'); setSaving(false); }
  };

  return (
    <BoardModal open title="Link Existing Group" width={520} onClose={onClose}
      footer={<BMCancelButton onClick={onClose} disabled={saving}>Close</BMCancelButton>}>
      <label style={BM_LABEL}>Search Microsoft 365 Groups</label>
      <input autoFocus value={term} onChange={e => { setTerm(e.target.value); run(e.target.value); }}
        placeholder="Type a group name…" style={BM_INPUT} />
      {error && <div style={{ marginTop: 10, color: '#DC2626', fontSize: 12 }}>{error}</div>}
      <div style={{ marginTop: 12, maxHeight: 320, overflowY: 'auto' }}>
        {searching && <div style={{ padding: 10, color: 'var(--ink-4)', fontSize: 13 }}>Searching…</div>}
        {!searching && term.trim().length >= 2 && results.length === 0 && !error && (
          <div style={{ padding: 10, color: 'var(--ink-4)', fontSize: 13 }}>No groups found.</div>
        )}
        {results.map(g => (
          <button key={g.id} type="button" disabled={saving} onClick={() => pick(g)}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', borderBottom: '0.5px solid #EAECF0', background: 'transparent', cursor: saving ? 'default' : 'pointer', borderRadius: 6 }}
            onMouseEnter={e => { if (!saving) e.currentTarget.style.background = '#F5F6F8'; }}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
            <div style={{ fontSize: 14, color: '#0F1117', fontWeight: 500 }}>{g.displayName}</div>
            {g.mail && <div style={{ fontSize: 12, color: '#9198A8', fontFamily: 'var(--font-mono)' }}>{g.mail}</div>}
          </button>
        ))}
      </div>
    </BoardModal>
  );
}
